// ── useGameStatesPoll — value-dashboard's batched gameState poller ─
//
// Polls hydra's /api/analytics/game-states at 5s while mounted, scoped
// to the gameKeys it's handed. Result lands in `gameStatesStore` for
// any consumer to read.
//
// Chunks the key list at 50/request (hydra's hard cap). The dashboard
// rarely shows more than 30 games at once but the chunker keeps us
// safe for big slate days.
//
// Pauses on `document.hidden` so background tabs don't burn the
// endpoint. Resumes on visibility-change → triggers an immediate fetch
// so a returning tab sees fresh state instead of waiting up to 5s.

import { useEffect, useMemo } from 'react';
import { lookupCoreGameState, useCoreGameStateInterests, useCoreGameStatesStore } from '@/services/coreGameStates';
import { primaryGameStateEnabled } from '@/services/primaryGameState';
import type { AdmittedGameState } from '@/lib/gameState/admission';
import {
  GAME_STATES_BATCH_CAP,
  getGameStates,
  hasGameStateAnalytics,
  type UnabatedGameState,
} from '../services/hydraAnalytics';
import { useGameStatesStore } from '../stores/gameStatesStore';
import { useMarketStore } from '../stores/marketStore';

const POLL_MS = 5_000;

/** Omission is not withdrawal: this endpoint promises one explicit value
 * (including null) for every requested key. Reject a partial batch atomically.
 */
export function completeGameStateBatch(
  keys: readonly string[], raw: unknown,
): { states: Record<string, UnabatedGameState | null>; serverTs: number | null; invalidKeys: string[] } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid gameStates response');
  const envelope = raw as Record<string, unknown>;
  if (!envelope.states || typeof envelope.states !== 'object' || Array.isArray(envelope.states)) {
    throw new Error('Invalid gameStates map');
  }
  const states = envelope.states as Record<string, unknown>;
  const admitted: Record<string, UnabatedGameState | null> = {};
  const invalidKeys: string[] = [];
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(states, key)) throw new Error('Incomplete gameStates response');
    const value = states[key];
    if (value !== null) {
      const record = value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown> : null;
      if (!record || ![1, 2, 3].includes(record.statusId as number)
        || !['awayScore', 'homeScore'].every(field => record[field] === null
          || (typeof record[field] === 'number' && Number.isFinite(record[field]) && (record[field] as number) >= 0))
        || typeof record.lastUpdateMs !== 'number' || !Number.isFinite(record.lastUpdateMs)
        || record.lastUpdateMs <= 0) {
        // A malformed game cannot certify itself or strand every other game.
        // Publish unknown for this key, retaining the diagnostic separately
        // from an explicit producer null. Batch membership remains atomic.
        admitted[key] = null;
        invalidKeys.push(key);
        continue;
      }
    }
    admitted[key] = value as UnabatedGameState | null;
  }
  // Older servers use serverTs; Core uses generatedAt. Missing publication
  // remains missing. Neither is the provider's lastUpdateMs.
  const publication = typeof envelope.serverTs === 'number' ? envelope.serverTs
    : typeof envelope.generatedAt === 'string' ? Date.parse(envelope.generatedAt) : NaN;
  return { states: admitted, serverTs: Number.isFinite(publication) && publication > 0 ? publication : null, invalidKeys };
}

export function useGameStatesPoll(gameKeys: string[]): void {
  // Build the configured Core comparison baseline for the same visible slate.
  // Legacy remains the display/lifecycle authority until the primary adapter
  // handles per-game freshness, withdrawal and non-confirmed zombie finals.
  useCoreGameStateInterests(gameKeys);
  // Serialize whole keys; joining on '|' collides with canonical key parts.
  const stableKey = useMemo(
    () => JSON.stringify([...new Set(gameKeys)].sort()),
    [gameKeys],
  );

  useEffect(() => {
    if (primaryGameStateEnabled() || !hasGameStateAnalytics()) return;
    const keys = JSON.parse(stableKey) as string[];
    let cancelled = false;
    let inFlight = false;
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (cancelled) return;
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = setTimeout(tick, POLL_MS);
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      if (document.hidden) {
        // Skip while tab is hidden; visibility-change handler triggers
        // the next fetch when we come back.
        scheduleNext();
        return;
      }
      if (keys.length === 0) {
        useGameStatesStore.getState().setStates({}, null);
        scheduleNext();
        return;
      }
      inFlight = true;
      try {
        // Chunk at 50 to respect hydra's cap. Most dashboards stay
        // under 50 so this is usually a single request.
        const chunks: string[][] = [];
        for (let i = 0; i < keys.length; i += GAME_STATES_BATCH_CAP) {
          chunks.push(keys.slice(i, i + GAME_STATES_BATCH_CAP));
        }
        const results = await Promise.all(chunks.map(async (chunk) =>
          completeGameStateBatch(chunk, await getGameStates(chunk, controller.signal))));
        if (cancelled) return;
        const merged: Record<string, UnabatedGameState | null> = {};
        const invalidKeys: string[] = [];
        let serverTs: number | null = null;
        for (const r of results) {
          for (const [k, v] of Object.entries(r.states)) merged[k] = v;
          invalidKeys.push(...r.invalidKeys);
          if (r.serverTs !== null) serverTs = Math.max(serverTs ?? 0, r.serverTs);
        }
        useGameStatesStore.getState().setStates(merged, serverTs, Date.now(), invalidKeys);
        // Fan Unabated's eventStart into marketStore's canonical-key →
        // startTime cache. The store keeps per-source slots resolved by
        // precedence: post-start Unabated outranks the venues (live-delay
        // timing wins); pre-start it slots between kalshi and polymarket,
        // so venue schedule corrections still propagate. Each poll cycle
        // re-seeds the slot. Lets spreads/totals + niche-sport moneylines
        // pick up an event start for GTS expiry, countdown chips,
        // near-tip pulse, etc.
        const seed = useMarketStore.getState().seedEventStartTimeMsByKey;
        for (const [gameKey, state] of Object.entries(merged)) {
          if (!state || !state.eventStart) continue;
          const ms = Date.parse(state.eventStart);
          if (Number.isFinite(ms) && (state.statusId === 1 || state.statusId === 2 || state.statusId === 3)) {
            seed(gameKey, ms, state.statusId);
          }
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'gameStates fetch failed';
        useGameStatesStore.getState().setError(msg);
      } finally {
        inFlight = false;
      }
      scheduleNext();
    };

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        if (timeoutId != null) clearTimeout(timeoutId);
        void tick();
      }
    };

    void tick();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId != null) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [stableKey]);

  // Core primary mode issues no legacy poll, so the start-time seeding above
  // never runs. Seed the same slot from the Core game-state records for these
  // keys instead, again on every record change (the poll re-seeded every
  // cycle). A withdrawn or foreign-keyed record seeds nothing. The record is
  // looked up in Core's spelling (a `cfb` row reads `ncaaf`); the slot it
  // seeds is the caller's own key, the one marketStore reads back.
  useEffect(() => {
    if (!primaryGameStateEnabled()) return;
    const keys = JSON.parse(stableKey) as string[];
    const seedFrom = (records: ReadonlyMap<string, AdmittedGameState>) => {
      const seed = useMarketStore.getState().seedEventStartTimeMsByKey;
      for (const key of keys) {
        const hit = lookupCoreGameState(records, key);
        const row = hit?.row;
        if (!row || row.withdrawal || row.state?.gameKey !== hit?.coreKey) continue;
        const ms = Date.parse(row.state.eventStart);
        if (Number.isFinite(ms)) seed(key, ms, row.state.statusId);
      }
    };
    seedFrom(useCoreGameStatesStore.getState().records);
    return useCoreGameStatesStore.subscribe((s, prev) => {
      if (s.records !== prev.records) seedFrom(s.records);
    });
  }, [stableKey]);
}
